import React from 'react'
import { isLoaded } from 'react-redux-firebase'
import { database, storage } from 'services/firebase'

import withStyles from '@material-ui/core/styles/withStyles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Icon from '@material-ui/core/Icon'

import EditDialog from 'components/Dialogs/EditDialog'
import PageLayout from 'components/Layout/PageLayout'
import ColorLegend from 'components/Collections/ColorLegend'
import SearchBar from 'components/Cards/SearchBar'
import CoursePanel from '../CoursePanel'

import { courseSchema, registrationSchema } from 'constants/schemas'
import filterByString from './helpers/filter-by-string'
import arrayToCsv from 'helpers/file/array-to-csv'
import dateString from 'helpers/generators/date-string'
import styles from './styles'

class CourseList extends React.Component {
  state = {
    attendee: null,
    course: null,
    copiedCourse: null,
    expanded: false,
    searchString: ''
  }

  handleChange = key => value => {
    this.setState({ [key]: value })
  }

  handleCourseCopy = course => () => {
    this.setState({
      copiedCourse: {
        ...course,
        attendees: undefined,
        archived: false,
        cancelled: false
      },
      course: database.ref('courses').push().key
    })
  }

  handleCourseAdd = () => {
    this.setState({
      course: database.ref('courses').push().key
    })
  }

  handleCourseArchive = (courseId, archive) => () => {
    database
      .ref('courses')
      .child(courseId)
      .update({ archive })
  }

  handleCourseSelect = id => () => {
    this.setState({ course: id })
  }

  handleCourseUpdate = id => course => {
    database
      .ref('courses')
      .child(id)
      .update(course)

    this.setState({
      course: null,
      copiedCourse: null
    })

    if (this.props.readOnly) {
      // We are on the course archive screen and a course is being copied,
      // redirect to the main courses page to see the new course
      this.props.history.push('/courses')
    }
  }

  handleCourseDelete = courseId => () => {
    const attendees = this.props.courses[courseId].attendees
    Object.keys(attendees || {}).forEach(attendeeId => {
      storage.ref(`signatures/${courseId}/${attendeeId}.png`).delete()
    })

    database
      .ref('courses')
      .child(courseId)
      .remove()

    this.handleCancel()
  }

  handleCancel = () => {
    this.setState({ attendee: null, course: null, copiedCourse: null })
  }

  handleExpand = key => (event, expanded) => {
    this.setState({
      expanded: expanded ? key : false
    })
  }

  handleCourseAttendeeDelete = (courseId, attendeeId) => () => {
    storage.ref(`signatures/${courseId}/${attendeeId}.png`).delete()

    database
      .ref(`courses/${courseId}/attendees`)
      .child(attendeeId)
      .remove()

    this.handleCancel()
  }

  handleCourseAttendeeSelect = (courseId, attendeeId) => () => {
    this.setState({
      attendee: {
        course: courseId,
        id: attendeeId
      }
    })
  }

  handleCourseAttendeeUpdate = (courseId, attendeeId) => attendee => {
    database
      .ref(`courses/${courseId}/attendees`)
      .child(attendeeId)
      .update(attendee)

    this.handleCancel()
  }

  handleCourseAttendeeDownload = courseId => () => {
    const course = this.props.courses[courseId]
    arrayToCsv(
      Object.values(course.attendees).map(attendee => {
        delete attendee.signature
        return attendee
      }),
      `${dateString()} - ${course.title}`
    )
  }

  handleEnterRegistrationMode = courseId => () => {
    database.ref(`users/${this.props.user.uid}`).update({
      registrationMode: courseId
    })
  }

  render() {
    const {
      attendee,
      course,
      copiedCourse,
      expanded,
      searchString
    } = this.state
    const {
      avatarIcon,
      classes,
      providers,
      courses,
      filter,
      sort,
      title,
      readOnly
    } = this.props

    if (!isLoaded(courses) || !isLoaded(providers)) {
      return <Typography>Loading</Typography>
    }

    return (
      <PageLayout
        noCard
        title={title}
        rightGutter={
          Object.keys(providers).length > 0 && (
            <ColorLegend
              items={Object.values(providers)}
              title="Provider Key"
            />
          )
        }
        rightGutterFixed
      >
        {(course || attendee) && (
          <EditDialog
            title={course ? 'Course Details' : 'Attendee Details'}
            cancel={this.handleCancel}
            current={
              course
                ? copiedCourse || courses[course]
                : courses[attendee.course].attendees[attendee.id]
            }
            itemLabel={course ? 'course' : 'attendee'}
            providers={providers}
            fields={course ? courseSchema : registrationSchema}
            update={
              (copiedCourse || !readOnly) &&
              (course
                ? this.handleCourseUpdate(course)
                : this.handleCourseAttendeeUpdate(attendee.course, attendee.id))
            }
            remove={
              !readOnly &&
              (course
                ? this.handleCourseDelete(course)
                : this.handleCourseAttendeeDelete(attendee.course, attendee.id))
            }
            attendeeId={attendee && attendee.id}
            courseId={attendee && attendee.course}
            readOnly={readOnly && !copiedCourse}
          />
        )}
        {/* Text search panel */}
        <div className={classes.searchInput}>
          <SearchBar
            onChange={this.handleChange('searchString')}
            value={searchString}
          />
        </div>
        {Object.keys(courses).length ? (
          <div className={classes.listRoot}>
            {Object.entries(courses)
              .filter(filterByString(searchString, filter))
              .sort(sort)
              .map(([courseId, course]) => (
                <CoursePanel
                  avatarIcon={avatarIcon}
                  course={course}
                  courseId={courseId}
                  expanded={expanded === courseId}
                  handleCourseArchive={this.handleCourseArchive}
                  handleCourseAttendeeDownload={
                    this.handleCourseAttendeeDownload
                  }
                  handleCourseAttendeeSelect={this.handleCourseAttendeeSelect}
                  handleCourseCopy={this.handleCourseCopy}
                  handleCourseSelect={this.handleCourseSelect}
                  handleEnterRegistrationMode={this.handleEnterRegistrationMode}
                  handleExpand={this.handleExpand}
                  provider={providers[course.provider]}
                  key={courseId}
                  readOnly={readOnly}
                />
              ))}
          </div>
        ) : (
          <Typography
            variant="subheading"
            align="center"
            style={{ paddingTop: 16 }}
          >
            No courses yet,
            {!Object.keys(providers).length ? (
              <span>
                {' '}
                add a provider in the{' '}
                <a
                  href="/providers"
                  onClick={event => {
                    event.preventDefault()
                    this.props.history.push('/providers')
                  }}
                >
                  providers
                </a>{' '}
                page to get started
              </span>
            ) : (
              ' try adding one now!'
            )}
          </Typography>
        )}
        <Button
          variant="fab"
          color="secondary"
          aria-label="add"
          className={classes.addButton}
          onClick={this.handleCourseAdd}
        >
          <Icon>add</Icon>
        </Button>
      </PageLayout>
    )
  }
}

export default withStyles(styles)(CourseList)



// WEBPACK FOOTER //
// ./src/views/CourseList/Main/index.jsx